import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  Grid,
  CardContent,
  TextField,
  FormHelperText,
  Button,
  Box
} from '@mui/material'
import { GenXDropdown } from '../../../../components/GenXDropdown'
import { fetchDeviceModel } from '../../redux/actionCreators'
import { type DeviceModelState } from '../../redux/deviceModelSlice'
import { type RootState } from '../../../../store'
import { connect, type ConnectedProps } from 'react-redux'
import LanguageStrings from '../../../../i18n/locales'
import InfoVector from '../../../../assets/InfoVector.png'
import { isValidDeviceData, isValidDevicePort, hasSpaceAtStartOrEnd, isValidLatLong, isValidSlaveAddress } from '../../../../utils/validator'
import { styled } from '@mui/system'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Group540 from '../../../../assets/SelectDeviceType/Group540.png'
import errorCrossIcon from '../../../../assets/errorCrossIcon.svg'
import CustomToast from '../ToastComponent'
import AddDeviceDummy from '../../../../assets/AddDevice/image.png'
import { containerColumns, columnSpacing, elementColumns, CustomResponsiveGrid } from '../../../../utils/GlobalStyles'
import './AddDevice.css'

const MAX_LENGTH = 100
const APPLICATION_TYPE_MAX_LENGTH = 500

const DeviceInformationStrings = LanguageStrings().DeviceManagementCollectionStrings.DeviceInformationComponent

const cardStyles = {
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  marginTop: '24px',
  overflow: 'visible'
}

const infoCardStyles = {
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  marginTop: '24px'
}

const errorCardStyles = {
  borderRadius: '10px',
  border: '1px solid #101010',
  backgroundColor: '#272727',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
  marginTop: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '30px 0px 30px 0px'
}

const textStyle = {
  root: {
    backgroundColor: '#272727',
    '& .MuiInputBase-input': {
      color: '#F0F0F0',
      backgroundColor: '#404040'
    },
    '& .MuiInputLabel-root': {
      color: '#F0F0F0',
      '&.Mui-focused': {
        color: '#F0F0F0'
      },
      // Conditional color based on disabled state
      '&.Mui-disabled': {
        color: '#B0B0B0'
      }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#00000000'
      },
      '&:hover fieldset': {
        borderColor: '#00000000'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00000000'
      }
    },
    '& .Mui-disabled': {
      color: '#B0B0B0 !important'
    }
  },
  width: 'min(410px, 100%)'
}

const CustomCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '309px',
  height: '240px',
  borderRadius: '10px',
  border: '1px solid var(--General-Secondary, #303030)',
  background: 'var(--General-Secondary, #303030)',
  position: 'relative'
})

const FileInput = styled('input')({
  display: 'none'
})

const FileButton = styled(Button)({
  width: '309px',
  padding: '6px 24px',
  marginTop: '5px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  borderRadius: '4px',
  border:
    '1.5px solid var(--component-light-buttons-regular-primary-outline-blue-a-20071-b-3, #64C3FF)',
  background: '#202020',
  boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.30)'
})

interface DeviceType {
  asset_type_id: string
  asset_type_name: string
  category_type_id: string
  category_type_name: string
  isCustomDevice: boolean
  isDeviceModelApplicable: boolean
}
interface DeviceModel {
  assetModelId: string
  assetModel: string
  assetTypeId: string
}

interface ParentProps {
  deviceType: DeviceType[] | null
  deviceName: string
  setDeviceName: React.Dispatch<React.SetStateAction<string>>
  deviceNameErrorInit: boolean
  setDeviceNameErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  serialNumber: string
  setSerialNumber: React.Dispatch<React.SetStateAction<string>>
  serialNumberErrorInit: boolean
  setSerialNumberErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  applicationType: string
  setApplicationType: React.Dispatch<React.SetStateAction<string>>
  applicationTypeErrorInit: boolean
  setApplicationTypeErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  location: string
  setLocation: React.Dispatch<React.SetStateAction<string>>
  locationErrorInit: boolean
  setLocationErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  slaveAddress: string
  setSlaveAddress: React.Dispatch<React.SetStateAction<string>>
  slaveAddressErrorInit: boolean
  setSlaveAddressErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  previewImage: string | null
  setPreviewImage: React.Dispatch<React.SetStateAction<string | null>>
  assetType: LabelValue | null
  setAssetType: React.Dispatch<React.SetStateAction<LabelValue | null>>
  assetModel: LabelValue | null
  setAssetModel: React.Dispatch<React.SetStateAction<LabelValue | null>>
  deviceImage: any
  setDeviceImage: React.Dispatch<React.SetStateAction<any>>
  deviceId: string
  portNumber: string
  setPortNumber: React.Dispatch<React.SetStateAction<string>>
  portNumberErrorInit: boolean
  setPortNumberErrorInit: React.Dispatch<React.SetStateAction<boolean>>
  isTcpProtocol: boolean
}

interface LabelValue {
  label: string
  value: string
}

interface CustomLabelValue {
  label: string
  value: string
  mDisabled?: boolean
}

const DeviceInformation = (
  props: PropsFromRedux & ParentProps
): JSX.Element => {
  const [deviceTypeOptions, setDeviceTypeOptions] = useState<LabelValue[]>([])
  const [deviceModelOptions, setDeviceModelOptions] = useState<LabelValue[]>(
    []
  )
  const [imageDimensions, setImageDimensions] = useState({ width: '136px', height: '136px' })
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [customDevice, setCustomDevice] = useState<boolean>(true)

  useEffect(() => {
    // istanbul ignore next
    if (props.deviceImage !== undefined && props.deviceImage !== null) setImageDimensions({ width: '260px', height: '215px' })
  }, [])

  useEffect(() => {
    // istanbul ignore else
    if (props.deviceType !== null) {
      setDeviceTypeOptions(convertToDeviceTypeOptions(props.deviceType))
    }
  }, [props.deviceType])

  React.useEffect(() => {
    // istanbul ignore else
    if (props.deviceModel?.deviceModel != null && props.deviceModel.deviceModel.length > 0) {
      setDeviceModelOptions(
        convertToDeviceModelOptions(props.deviceModel.deviceModel)
      )
    }
  }, [props.deviceModel])

  const convertToDeviceTypeOptions = (deviceTypes: DeviceType[]): CustomLabelValue[] =>
    deviceTypes
      .map((deviceType) => ({
        label: deviceType.asset_type_name,
        value: deviceType.asset_type_id,
        mDisabled: deviceType.isCustomDevice
      }))

  const convertToDeviceModelOptions = (deviceModels: DeviceModel[]): LabelValue[] =>
    deviceModels.map((deviceModel) => ({
      label: deviceModel.assetModel,
      value: deviceModel.assetModelId
    }))

  const handleDeviceTypeSelect = (selectedDeviceType: CustomLabelValue): void => {
    props.setAssetType(selectedDeviceType)
    props.fetchDeviceModel(selectedDeviceType.value)
    setCustomDevice(selectedDeviceType?.mDisabled !== undefined ? selectedDeviceType.mDisabled : false)
    selectedDeviceType?.mDisabled === true ? window.sessionStorage.setItem('isCustomDevice', 'true') : window.sessionStorage.setItem('isCustomDevice', 'false')
  }

  const handleDeviceModelSelect = (selectedDeviceModel: LabelValue): void => {
    props.setAssetModel(selectedDeviceModel)
  }

  const isDeviceNameNotValid = (): boolean => {
    return (
      !isValidDeviceData(props.deviceName) || props.deviceName.length > MAX_LENGTH
    )
  }
  const isSerialNumberNotValid = (): boolean => {
    return (
      (props.serialNumber !== '' && (!isValidDeviceData(props.serialNumber) || props.serialNumber.length > MAX_LENGTH)) || false
    )
  }
  const isapplicationTypeNotValid = (): boolean => {
    return (
      (props?.applicationType !== '' && (!isValidDeviceData(props?.applicationType) || props?.applicationType?.length > APPLICATION_TYPE_MAX_LENGTH))
    )
  }
  const isLocationNotValid = (): boolean => {
    return (
      (props.location !== '' && (!isValidLatLong(props.location) || props.location.length === 0)) || false
    )
  }
  const isSlaveAddressNotValid = (): boolean => {
    return (!isValidSlaveAddress(props.slaveAddress) || props.slaveAddress.length === 0)
  }
  const isPortNumberNotValid = (): boolean => {
    const port = parseInt(props.portNumber, 10)
    return !(port > 0 && port <= 65535 && isValidDevicePort(props.portNumber) && props.portNumber.length <= 5)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const files = event.target.files
    const maxSize = 100 * 1024
    const allowedTypes = ['image/jpeg', 'image/png']
    const allowedExtensions = ['jpg', 'jpeg', 'png']
    const alphanumericRegex = /^[a-zA-Z0-9]+$/
    // istanbul ignore else
    if (files !== null && files.length > 0) {
      // Validate file size
      if (files[0].size > maxSize) {
        CustomToast({
          type: 'error',
          heading: DeviceInformationStrings.ImageSizeError,
          message: DeviceInformationStrings.ImageSizeErrorMsg
        })
        props.setPreviewImage(AddDeviceDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      // Validate file type
      const fileExtension = files[0].name.split('.').pop()?.toLowerCase()
      if (!allowedTypes.includes(files[0].type) || ((fileExtension != null) && !allowedExtensions.includes(fileExtension))) {
        CustomToast({
          type: 'error',
          heading: DeviceInformationStrings.ImageTypesError,
          message: DeviceInformationStrings.ImageTypesErrorMsg
        })
        props.setPreviewImage(AddDeviceDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      // Validate file name (without extension)
      const fileNameWithoutExtension = files[0].name.split('.')[0]
      /* istanbul ignore next */
      if (!alphanumericRegex.test(fileNameWithoutExtension)) {
        CustomToast({
          type: 'error',
          heading: 'Invalid File Name',
          message: 'File name should contain only alphanumeric characters.'
        })
        props.setPreviewImage(AddDeviceDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      /* istanbul ignore next */
      if (fileNameWithoutExtension.length > 20) {
        CustomToast({
          type: 'error',
          heading: 'Invalid File Name',
          message: 'File Name is greater than 20 alphanumeric characters.'
        })
        props.setPreviewImage(AddDeviceDummy)
        event.target.value = '' // Clearing the file input value
        return
      }
      const reader = new FileReader()
      // istanbul ignore next
      reader.onloadend = () => {
        const image = new Image()
        image.src = reader.result as string
        image.onload = () => {
          setImageDimensions({ width: '260px', height: '215px' }) // width: 309, height: 240
        }
        props.setPreviewImage(reader.result as string)
        props.setDeviceImage(files[0])
      }
      reader.readAsDataURL(files[0])
    }
  }

  const handleBrowseClick = (): void => {
    fileInputRef.current?.click()
  }

  const deviceNameErrorMessage = (): string | null => {
    if (props?.deviceName?.length > MAX_LENGTH || !isValidDeviceData(props?.deviceName)) {
      if (props?.deviceName?.length > MAX_LENGTH) {
        return DeviceInformationStrings.DeviceNameLengthError
      } else if (hasSpaceAtStartOrEnd(props?.deviceName)) {
        return DeviceInformationStrings.NotAllowedError
      } else if (!isValidDeviceData(props?.deviceName)) {
        return DeviceInformationStrings.DeviceNameErrorMsg
      }
    }
    return null
  }
  const serialNumberErrorMessage = (): string | null => {
    if (props?.serialNumber?.length > MAX_LENGTH || !isValidDeviceData(props?.serialNumber)) {
      if (props?.serialNumber?.length > MAX_LENGTH) {
        return DeviceInformationStrings.SerialNumberLengthError
      } else if (hasSpaceAtStartOrEnd(props?.serialNumber)) {
        return DeviceInformationStrings.NotAllowedError
      } else if (!isValidDeviceData(props?.serialNumber)) {
        return DeviceInformationStrings.SerialNumberErrorMsg
      }
    }
    return null
  }
  const applicationTypeErrorMessage = (): string | null => {
    if (props?.applicationType?.length > MAX_LENGTH || !isValidDeviceData(props?.applicationType)) {
      if (props?.applicationType?.length > APPLICATION_TYPE_MAX_LENGTH) {
        return DeviceInformationStrings.ApplicationTypeLengthError
      } else if (hasSpaceAtStartOrEnd(props?.applicationType)) {
        return DeviceInformationStrings.NotAllowedError
      } else if (!isValidDeviceData(props?.applicationType)) {
        return DeviceInformationStrings.ApplicationTypeErrorMsg
      }
    }
    return null
  }

  return (
    <div className="device-info">
      <CustomResponsiveGrid>
        <Card sx={cardStyles}>
          <CardContent>
            <div className="device-info-content">
              <p className="device-info-heading">
                {DeviceInformationStrings.DeviceInformation}
              </p>
            </div>
            <Grid className='responsive-grid' container spacing={columnSpacing} columns={containerColumns}>
              <Grid item {...elementColumns.oneColumn}>
                <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                  {DeviceInformationStrings.DeviceType}
                </span>
                <GenXDropdown
                  width={'100%'}
                  placeholder='Select a device type'
                  value={props.assetType}
                  options={deviceTypeOptions}
                  onSelect={handleDeviceTypeSelect}
                  data-testId="device-type-dropdown"
                />
              </Grid>
              { !customDevice &&
              <Grid item {...elementColumns.oneColumn}>
                <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                  {DeviceInformationStrings.DeviceModel}
                </span>
                <GenXDropdown
                  width={'100%'}
                  placeholder='Select a device model'
                  value={props.assetModel}
                  options={deviceModelOptions}
                  onSelect={handleDeviceModelSelect}
                  data-testId="device-model-dropdown"
                />
              </Grid>
              }
              {/* Empty space for consistent layout like KPI Information */}
              <Grid item {...elementColumns.oneColumn}>
                {/* Reserved space for future field or maintaining layout balance */}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </CustomResponsiveGrid>
      {(props.assetModel === null && !customDevice) || props.assetType === null
        ? (
          <Card sx={errorCardStyles}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                width: '679px',
                height: '261px',
                flexDirection: 'row',
                alignItems: 'center',
                flexShrink: 0,
                borderRadius: '10px',
                border: '1px solid #404040',
                background: '#404040'
              }}
            >
              <div>
                <img
                  style={{
                    width: '161.997px',
                    height: '193.975px',
                    flexShrink: 0
                  }}
                  src={Group540}
                />
              </div>
              <div>
                <div className="device-info-content">
                  <p className="device-info-heading">
                    {DeviceInformationStrings.SelectDeviceType}
                  </p>
                  <p
                    style={{
                      color:
                        'var(--typography-dark-base-default-f-0-f-0-f-0, #F0F0F0)',
                      fontFeatureSettings: "'clig' off, 'liga' off",
                      fontFamily: '"Honeywell Sans Web"',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '24px'
                    }}
                  >
                    This could be because:
                  </p>
                  <p
                    style={{
                      color:
                        'var(--typography-dark-base-default-f-0-f-0-f-0, #F0F0F0)',
                      fontFeatureSettings: "'clig' off, 'liga' off",
                      fontFamily: '"Honeywell Sans Web"',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '24px'
                    }}
                  >
                    . You have not selected any device type.
                  </p>
                </div>
              </div>
            </div>
          </Card>
          )
        : (
          <Card sx={infoCardStyles}>
            <CardContent>
              <div className="device-info-content">
                <p className="device-info-heading">
                  {DeviceInformationStrings.DeviceDetails}
                </p>
              </div>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, marginTop: '10px' }}>
                <div className="image-section" style={{ backgroundColor: '#202020', marginRight: '20px', padding: '20px', width: 'max-content' }}>
                  <CustomCardContent>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      {props.previewImage !== null && (
                        <img
                          src={props.previewImage}
                          alt="Preview"
                          style={{ width: imageDimensions.width, height: imageDimensions.height }}
                        />
                      )}
                    </div>
                    <FileInput
                      ref={fileInputRef}
                      type="file"
                      id="fileInput"
                      data-testid="browse-image-file"
                      accept="image/jpeg, image/png"
                      onChange={handleFileChange}
                    />
                  </CustomCardContent>
                  <FileButton
                    data-testid="browse-image-button"
                    onClick={handleBrowseClick}
                    style={{
                      fontFamily: 'Honeywell Sans Web',
                      textTransform: 'unset'
                    }}
                  >
                    Add Photo
                  </FileButton>
                </div>
                <Grid container className="card-grid" height={'fit-content'} >
                  <Grid container spacing={columnSpacing} rowSpacing={1} className='responsive-grid'>
                    <Grid item xs={12}>
                      <span style={{ fontSize: '15px', padding: '5px 3px', display: 'block', color: '#F0F0F0' }}>
                        {DeviceInformationStrings.DeviceName}
                        <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                      </span>
                      <TextField
                        id="custom-textfield"
                        variant="outlined"
                        data-testId="device-name"
                        placeholder="Enter Device Name"
                        InputLabelProps={{
                          disabled: true
                        }}
                        value={props.deviceName}
                        onChange={(e) => {
                          props.setDeviceName(e.target.value)
                          props.setDeviceNameErrorInit(true)
                        }}
                        disabled={props.deviceId !== ''}
                        size="small"
                        autoComplete="off"
                        className={`custom-textfield ${isDeviceNameNotValid() && props.deviceNameErrorInit ? 'error' : ''}`}
                        sx={{
                          ...textStyle.root,
                          width: 'min(410px, 100%)'
                        }}
                      />
                      {isDeviceNameNotValid() && props.deviceNameErrorInit && (
                          <Box sx={{ display: 'flex', width: 'min(410px, 100%)' }}>
                            <div style={{ marginTop: '3px' }}>
                              <img src={errorCrossIcon} alt="Error" />
                            </div>
                            <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '5px'
                                }}
                                className="error-msg"
                            >
                              {deviceNameErrorMessage()}
                            </FormHelperText>
                          </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <span style={{ fontSize: '15px', padding: '5px 3px', display: 'block', color: '#F0F0F0' }}>
                        {DeviceInformationStrings.SerialNumber}
                        <span style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}> - Optional</span>
                      </span>
                      <TextField
                        id="custom-textfield-serial-number"
                        variant="outlined"
                        data-testId="serial-number"
                        placeholder="Enter Serial Number"
                        InputLabelProps={{
                          disabled: true
                        }}
                        disabled={props.deviceId !== ''}
                        value={props.serialNumber}
                        onChange={(e) => {
                          props.setSerialNumber(e.target.value)
                          props.setSerialNumberErrorInit(true)
                        }}
                        size="small"
                        autoComplete="off"
                        className={`custom-textfield ${isSerialNumberNotValid() && props.serialNumberErrorInit ? 'error' : ''}`}
                        sx={{
                          ...textStyle.root,
                          width: 'min(410px, 100%)'
                        }}
                      />
                      {isSerialNumberNotValid() && props.serialNumberErrorInit && (
                          <Box sx={{ display: 'flex', width: 'min(410px, 100%)' }}>
                            <div style={{ marginTop: '3px' }}>
                              <img src={errorCrossIcon} alt="Error" />
                            </div>
                            <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '5px'
                                }}
                                className="error-msg"
                            >
                              {serialNumberErrorMessage()}
                            </FormHelperText>
                          </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <span style={{ fontSize: '15px', padding: '5px 3px', display: 'block', color: '#F0F0F0' }}>
                        {DeviceInformationStrings.ApplicationType}
                        <span style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}> - Optional</span>
                      </span>
                      <TextField
                        id="custom-textfield-application-type"
                        variant="outlined"
                        data-testId="application-type"
                        placeholder="Enter Application Type"
                        InputLabelProps={{
                          disabled: true
                        }}
                        disabled={props.deviceId !== ''}
                        value={props.applicationType}
                        onChange={(e) => {
                          props.setApplicationType(e.target.value)
                          props.setApplicationTypeErrorInit(true)
                        }}
                        size="small"
                        autoComplete="off"
                        className={`custom-textfield ${isapplicationTypeNotValid() && props.applicationTypeErrorInit ? 'error' : ''}`}
                        sx={{
                          ...textStyle.root,
                          width: 'min(410px, 100%)'
                        }}
                      />
                      {isapplicationTypeNotValid() && props.applicationTypeErrorInit && (
                          <Box sx={{ display: 'flex', width: 'min(410px, 100%)' }}>
                            <div style={{ marginTop: '3px' }}>
                              <img src={errorCrossIcon} alt="Error" />
                            </div>
                            <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '5px'
                                }}
                                className="error-msg"
                            >
                              {applicationTypeErrorMessage()}
                            </FormHelperText>
                          </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                        {DeviceInformationStrings.Location}
                        <span style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}> - Optional</span>
                      </span>
                      <TextField
                        id="custom-textfield-location"
                        variant="outlined"
                        data-testId="location"
                        placeholder="Example: 37.7749, -122.4194"
                        InputLabelProps={{
                          disabled: true
                        }}
                        disabled={props.deviceId !== ''}
                        value={props.location}
                        onChange={(e) => {
                          props.setLocation(e.target.value)
                          props.setLocationErrorInit(true)
                        }}
                        size="small"
                        autoComplete="off"
                        className={`custom-textfield ${isLocationNotValid() && props.locationErrorInit ? 'error' : ''}`}
                        sx={{
                          ...textStyle.root,
                          width: 'min(410px, 100%)'
                        }}
                      />
                      {isLocationNotValid() && props.locationErrorInit && (
                          <Box sx={{ display: 'flex', width: 'min(410px, 100%)' }}>
                            <div style={{ marginTop: '3px' }}>
                              <img src={errorCrossIcon} alt="Error" />
                            </div>
                            <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '5px'
                                }}
                                className="error-msg"
                            >
                              {DeviceInformationStrings.LocationError}
                            </FormHelperText>
                          </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                        {DeviceInformationStrings.SlaveAddress}
                        <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                        <Tooltip title={DeviceInformationStrings.SlaveAddressTooltip} placement="right" arrow>
                          <IconButton size="small">
                            <img style={{ width: '12px', height: '12px', marginLeft: '5px' }} src={InfoVector} />
                          </IconButton>
                        </Tooltip>
                      </span>
                      <TextField
                        id="custom-textfield-slave-address"
                        variant="outlined"
                        data-testid="custom-textfield-slave-address"
                        placeholder="Enter Slave Address"
                        InputLabelProps={{
                          disabled: true
                        }}
                        disabled={props.deviceId !== ''}
                        value={props.slaveAddress}
                        onChange={(e) => {
                          props.setSlaveAddress(e.target.value)
                          props.setSlaveAddressErrorInit(true)
                        }}
                        size="small"
                        autoComplete="off"
                        className={`custom-textfield ${isSlaveAddressNotValid() && props.slaveAddressErrorInit ? 'error' : ''}`}
                        sx={{
                          ...textStyle.root,
                          width: 'min(410px, 100%)'
                        }}
                      />
                      {isSlaveAddressNotValid() && props.slaveAddressErrorInit && (
                          <Box sx={{ display: 'flex', width: 'min(410px, 100%)' }}>
                            <div style={{ marginTop: '3px' }}>
                              <img src={errorCrossIcon} alt="Error" />
                            </div>
                            <FormHelperText
                                sx={{
                                  color: '#EE3426',
                                  paddingLeft: '5px'
                                }}
                                className="error-msg"
                            >
                              {DeviceInformationStrings.SlaveAddressError}
                            </FormHelperText>
                          </Box>
                      )}
                    </Grid>
                    {props.isTcpProtocol && (
                        <Grid item xs={12}>
                          <span style={{ fontSize: '15px', padding: '10px 3px', display: 'block', color: '#F0F0F0' }}>
                            {DeviceInformationStrings.PortNumber}
                            <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500', lineHeight: '24px' }}>*</sup>
                          </span>
                          <TextField
                            id="custom-textfield-port-number"
                            variant="outlined"
                            data-testid="custom-textfield-port-number"
                            placeholder="Enter Port Number"
                            InputLabelProps={{
                              disabled: true
                            }}
                            value={props.portNumber}
                            onChange={(e) => {
                              props.setPortNumber(e.target.value)
                              props.setPortNumberErrorInit(true)
                            }}
                            disabled={props.deviceId !== ''}
                            size="small"
                            autoComplete="off"
                            className={`custom-textfield ${isPortNumberNotValid() && props.portNumberErrorInit ? 'error' : ''}`}
                            sx={{
                              ...textStyle.root,
                              width: 'min(410px, 100%)'
                            }}
                          />
                          {isPortNumberNotValid() && props.portNumberErrorInit && (
                            <Box sx={{ display: 'flex', width: 'min(410px, 100%)' }}>
                              <div style={{ marginTop: '3px' }}><img src={errorCrossIcon} alt="Error" /></div>
                              <FormHelperText sx={{ color: '#EE3426', paddingLeft: '5px' }} className="error-msg">
                                {DeviceInformationStrings.PortNumberError}
                              </FormHelperText>
                            </Box>
                          )}
                        </Grid>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
          )}
    </div>
  )
}

interface DispatchToProps {
  fetchDeviceModel: (deviceTypeId: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchDeviceModel: (deviceTypeId: string) =>
    dispatch(fetchDeviceModel(deviceTypeId))
})

interface StateToProps {
  deviceModel: DeviceModelState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  deviceModel: state.deviceModel
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(DeviceInformation)
